/**
 * LanguageWarnings — headless controller for the two language-related
 * notices that used to live in the legacy EJS layer:
 *
 * 1. Context-vs-user language mismatch (was `FirstEntryLogic.ejs`, then a
 *    bridge-based port in `FirstEntry/index.jsx`): the graph's lemmatizer
 *    language differs from the user's account language, so results can be
 *    inconsistent. Skipped when the context is on "auto" (the server then
 *    follows the user's own setting — no real mismatch) and for the
 *    harmless default combo of an English graph viewed with "auto".
 *    Capped at MAX_LANGUAGE_WARNINGS impressions ever (localStorage).
 *
 * 2. Suspiciously short top words (was `GraphModularity.ejs`): when the
 *    average length of the top concepts is ≤ SHORT_AVERAGE_WORD_LENGTH
 *    characters, the lemmatizer most likely processed the text with the
 *    wrong language and chopped the words. Skipped for languages where
 *    short tokens are normal (hieroglyphic/non-Latin scripts) and when
 *    lemmatization is off ("zz" — short words are then genuine).
 *
 * At most one of the two shows per page load (they overlap in meaning and
 * the snackbar displays a single message anyway); the mismatch notice wins
 * because it names the exact cause. Both are owner-only: a public viewer
 * can change neither the context nor the "user language" the messages
 * point to.
 *
 * Data sources are React-native where possible (user language from
 * useBootstrapStore, top words from useGraphStore); the context language
 * has no store yet, so it's read through the same legacy bridge the
 * LanguageSettingsDialog uses.
 */
import * as React from 'react'

import { useSnackbar } from '../Snackbar'
import { useGraphStore } from '../../stores/useGraphStore'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'
import { getContextOption } from '../LeftMenuMigration/legacyBridge'

const LANGUAGE_WARNING_STORAGE_KEY = 'languageWarning'
const MAX_LANGUAGE_WARNINGS = 3

const TOP_NODES_SAMPLE_SIZE = 3
const SHORT_AVERAGE_WORD_LENGTH = 3

// Languages where 1–3 character top words are expected, not a lemmatizer
// accident: CJK scripts, Semitic scripts (3-letter roots), and 'zz'
// (lemmatization off — short words are the user's own).
const SHORT_WORDS_EXEMPT_LANGUAGES = new Set([
    'cn',
    'tw',
    'jp',
    'ko',
    'ar',
    'he',
    'zz',
])

function normalizeLanguage(value) {
    return value ? String(value).toLowerCase() : 'auto'
}

function getUserLanguage() {
    return normalizeLanguage(useBootstrapStore.getState().user?.inlanguage)
}

function getContextLanguage() {
    return normalizeLanguage(getContextOption('language'))
}

const selectTopNodes = state =>
    state.graphData?.entriesAndGraphOfContext?.graph?.graphologyGraph
        ?.attributes?.top_nodes

export default function LanguageWarnings() {
    const isPublic = useContextsStore(selectIsPublicView)
    const { location } = usePageInfo()
    const topNodes = useGraphStore(selectTopNodes)
    const showSnackbar = useSnackbar(state => state.showSnackbar)

    // One language warning per page load, shared by both checks.
    const hasWarned = React.useRef(false)

    // 1. Context-vs-user language mismatch (once per mount, capped ever).
    React.useEffect(() => {
        if (location !== 'context' || isPublic || hasWarned.current) return

        const contextLanguage = getContextLanguage()
        const userLanguage = getUserLanguage()

        if (contextLanguage === 'auto') return
        if (contextLanguage === userLanguage) return
        if (userLanguage === 'auto' && contextLanguage === 'en') return

        const timesShown =
            parseInt(localStorage.getItem(LANGUAGE_WARNING_STORAGE_KEY), 10) ||
            0
        if (timesShown >= MAX_LANGUAGE_WARNINGS) return
        localStorage.setItem(
            LANGUAGE_WARNING_STORAGE_KEY,
            String(timesShown + 1)
        )

        hasWarned.current = true
        showSnackbar(
            'info',
            `The language of the context is set to "${contextLanguage}" but your user language is "${userLanguage}". You can set your user language to "${contextLanguage}" to get more consistent results.`,
            'top',
            8000
        )
    }, [location, isPublic, showSnackbar])

    // 2. Suspiciously short top words (re-checked when the graph changes,
    // still at most once per page load).
    React.useEffect(() => {
        if (location !== 'context' || isPublic || hasWarned.current) return
        if (!Array.isArray(topNodes) || topNodes.length < TOP_NODES_SAMPLE_SIZE)
            return

        const names = topNodes
            .slice(0, TOP_NODES_SAMPLE_SIZE)
            .map(name => String(name).replace(/^\[\[|\]\]$/g, ''))
        const averageLength =
            names.reduce((total, name) => total + name.length, 0) /
            names.length
        if (averageLength > SHORT_AVERAGE_WORD_LENGTH) return

        const contextLanguage = getContextLanguage()
        const userLanguage = getUserLanguage()
        if (
            SHORT_WORDS_EXEMPT_LANGUAGES.has(contextLanguage) ||
            SHORT_WORDS_EXEMPT_LANGUAGES.has(userLanguage)
        )
            return

        // The language the lemmatizer actually ran with: the per-graph
        // setting wins over the account-wide one (lib/context.js seeds a
        // new context's language from the user's, then the context's own
        // setting is authoritative).
        const effectiveLanguage =
            contextLanguage !== 'auto' ? contextLanguage : userLanguage

        hasWarned.current = true
        showSnackbar(
            'warning',
            `The top words in your graph are suspiciously short. This may be because the processing language is set to "${effectiveLanguage}" but your content is in another language. You can change it in this graph's language settings or in your account settings.`,
            'top',
            12000
        )
    }, [topNodes, location, isPublic, showSnackbar])

    return null
}
